import { CloseApiClient } from "@app/closeApiClient";
import { downloadAsCsvFile } from "@app/csvHelper";
import { Lead } from "@app/types/lead";
import { Contact } from "@app/types/contact";
import {
  DeduplicateField,
  FormValues,
} from "@app/tools/contacts/findContactDuplicates/findContactDuplicatesPage";
import { snakeCaseToTitleCase } from "@app/utils/textUtils";

const deduplicateByToQuery: Record<DeduplicateField, any> = {
  contact_name: {
    type: "has_related",
    this_object_type: "lead",
    related_object_type: "contact",
    related_query: {
      type: "field_condition",
      field: {
        field_name: "name",
        object_type: "contact",
        type: "regular_field",
      },
      condition: {
        type: "exists",
      },
    },
  },
  email_address: {
    type: "field_condition",
    field: {
      field_name: "num_email_addresses",
      object_type: "lead",
      type: "regular_field",
    },
    condition: {
      gt: 1,
      type: "number_range",
    },
  },
  phone_number: {
    type: "field_condition",
    field: {
      field_name: "num_phone_numbers",
      object_type: "lead",
      type: "regular_field",
    },
    condition: {
      gt: 1,
      type: "number_range",
    },
  },
};

function buildSearchQuery(deduplicateBy: DeduplicateField) {
  return {
    type: "and",
    queries: [
      {
        object_type: "lead",
        type: "object_type",
      },
      {
        type: "field_condition",
        field: {
          field_name: "num_contacts",
          object_type: "lead",
          type: "regular_field",
        },
        condition: {
          gt: 1,
          type: "number_range",
        },
      },
      deduplicateByToQuery[deduplicateBy],
    ],
  };
}

function buildCountSearchQuery(deduplicateBy: DeduplicateField) {
  return {
    _limit: 1,
    include_counts: true,
    query: buildSearchQuery(deduplicateBy),
  };
}

async function getLeadCount(
  api: CloseApiClient,
  deduplicateBy: DeduplicateField,
): Promise<number> {
  const searchQuery = buildCountSearchQuery(deduplicateBy);
  const response = await api.search(searchQuery);
  return response.count.total;
}

async function fetchLeads(
  api: CloseApiClient,
  deduplicateBy: DeduplicateField,
  addOutputLine: (message: string) => void,
): Promise<Lead[]> {
  const searchQuery = {
    query: buildSearchQuery(deduplicateBy),
    _fields: {
      lead: ["id", "display_name", "contacts"],
    },
    include_counts: true,
  };

  const leads = [];
  for await (const lead of await api.searchAll(searchQuery, addOutputLine)) {
    leads.push(lead);
  }

  addOutputLine(`Found ${leads.length} leads.`);
  return leads;
}

function findContactDuplicatesInLead(
  lead: Lead,
  deduplicateBy: DeduplicateField,
): Record<string, Contact[]> {
  const duplicates: Record<string, Contact[]> = {};

  function addDuplicate(key: string, contact: Contact) {
    duplicates[key] = duplicates[key] || [];
    duplicates[key].push(contact);
  }

  for (const contact of lead.contacts) {
    if (deduplicateBy === "contact_name" && contact.name) {
      const name = contact.name.trim().toLowerCase();
      addDuplicate(name, contact);
    } else if (deduplicateBy === "email_address") {
      contact.emails.forEach((email) => addDuplicate(email.email, contact));
    } else if (deduplicateBy === "phone_number") {
      contact.phones.forEach((phone) => addDuplicate(phone.phone, contact));
    }
  }

  // Filter out non-duplicates
  return Object.fromEntries(
    Object.entries(duplicates).filter(([, contacts]) => contacts.length > 1),
  );
}

function generateCsvRow(
  contact: Contact,
  deduplicateBy: DeduplicateField,
): Record<string, string> {
  // Add deduplication attribute first (name, email address, or phone number)
  //
  // Contact already has these, but they aren't quite the same:
  //  - deduplicated `name` is all lowercase vs. original contact `name`
  //  - deduplicated `email` is a single email address vs. a list of contact `emails`
  //  - deduplicated `phone` is a single phone number vs. a list of contact `phones`
  const csvRow: Record<string, string> = {
    [snakeCaseToTitleCase(deduplicateBy)]: contact[deduplicateBy],
    "Contact ID": contact.id,
    Name: contact.name || "",
    "Date Created": contact.date_created,
    "Lead ID": contact.lead_id,
    Title: contact.title || "",
    Emails: contact.emails.map((email) => email.email).join(", "),
    Phones: contact.phones.map((phone) => phone.phone).join(", "),
    URLs: contact.urls.map((url) => url.url).join(", "),
  };

  return csvRow;
}

function findContactDuplicatesInLeads(
  leads: Lead[],
  deduplicateBy: DeduplicateField,
) {
  const duplicates: Record<string, Contact[]> = {};
  for (const lead of leads) {
    const leadDuplicates = findContactDuplicatesInLead(lead, deduplicateBy);
    Object.entries(leadDuplicates).forEach(([key, contacts]) => {
      duplicates[key] = [...(duplicates[key] || []), ...contacts];
    });
  }
  return duplicates;
}

function writeDuplicatesToCsv(
  organizationName: string,
  allDuplicates: Record<string, Contact[]>,
  deduplicateBy: DeduplicateField,
  addOutputLine: (message: string) => void,
) {
  const csvDuplicates = Object.entries(allDuplicates).flatMap(
    ([dedupeKey, contacts]) =>
      contacts.map((contact) => {
        const enrichedContact = { ...contact, [deduplicateBy]: dedupeKey };
        return generateCsvRow(enrichedContact, deduplicateBy);
      }),
  );

  // Sort duplicates by dedupe field so they are grouped together
  const deduplicateByFormatted = snakeCaseToTitleCase(deduplicateBy);
  csvDuplicates.sort((a, b) =>
    a[deduplicateByFormatted]!.toLowerCase().localeCompare(
      b[deduplicateByFormatted]!.toLowerCase(),
    ),
  );

  addOutputLine(`Writing duplicates to CSV...`);
  const fileName = `${organizationName} ${deduplicateByFormatted} Duplicates`;
  downloadAsCsvFile(fileName, csvDuplicates);
}

function getNumberOfDuplicates(allDuplicates: Record<string, Contact[]>) {
  return Object.values(allDuplicates).reduce(
    (total, contacts) => total + contacts.length,
    0,
  );
}

export async function findContactDuplicates(
  values: FormValues,
  addOutputLine: (message: string) => void,
  clearOutput: () => void,
): Promise<void> {
  clearOutput();

  const api = new CloseApiClient(values.apiKey);
  const organizationName = await api.getOrganizationName();
  const deduplicateBy = values.deduplicateBy;

  addOutputLine(
    `Finding contact duplicates by ${snakeCaseToTitleCase(deduplicateBy).toLowerCase()} in '${organizationName}'...`,
  );

  const leadCount = await getLeadCount(api, deduplicateBy);
  addOutputLine(
    `Fetching ${leadCount} leads (this may take a ${leadCount < 10000 ? "couple" : "couple dozen"} minutes to complete)...`,
  );

  const leads = await fetchLeads(api, deduplicateBy, addOutputLine);
  addOutputLine(`Finding duplicates...`);

  const allDuplicates = findContactDuplicatesInLeads(leads, deduplicateBy);
  const duplicateCount = getNumberOfDuplicates(allDuplicates);
  if (duplicateCount === 0) {
    addOutputLine("No duplicates found.");
    addOutputLine("Done!");
    return;
  }

  addOutputLine(`Found ${duplicateCount} duplicate contacts.`);

  writeDuplicatesToCsv(
    organizationName,
    allDuplicates,
    deduplicateBy,
    addOutputLine,
  );

  addOutputLine("Done!");
}
